import { computed, DestroyRef, effect, inject, Injectable, signal } from '@angular/core';
import { rxResource, takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { PolicyClient } from 'api-clients/policy-api-client';
import { catchError, filter, from, of } from 'rxjs';
import { EvaSidebarService } from 'src/app/eva-sidebar/eva-sidebar.service';
import { ClearedAnchorSnapshot, EvaAnchor, ResolvedContextSnapshot } from 'src/app/eva-sidebar/shared/models/eva-page-context.model';
import { AccountContextService } from 'src/shared/account/account-context.service';
import { PolicyContextService } from 'src/shared/policy/policy-context.service';
import { UserService } from 'src/shared/user.service';
import { globalWindow } from 'src/shared/window-globals';

@Injectable()
export class EvaPageContextService {
  readonly #userService = inject(UserService);

  readonly #manualOverrideCacheKey = `evaManualAnchor_${this.#userService.user.id}`;
  readonly #lastKnownAnchorCacheKey = `evaLastKnownAnchor_${this.#userService.user.id}`;
  readonly #resolvedContextCacheKey = `evaResolvedContext_${this.#userService.user.id}`;

  readonly #router = inject(Router, { optional: true });
  readonly #evaSidebarService = inject(EvaSidebarService);
  readonly #policyClient = inject(PolicyClient);
  readonly #policyContextService = inject(PolicyContextService);
  readonly #accountContextService = inject(AccountContextService);
  readonly #destroyRef = inject(DestroyRef);

  readonly #clearedAnchorSnapshot = signal<ClearedAnchorSnapshot | null>(this.#readLocalStorage(this.#manualOverrideCacheKey));

  readonly #restoredResolvedSnapshot = this.#readSessionCache<ResolvedContextSnapshot>(this.#resolvedContextCacheKey);

  readonly #policyContext = toSignal(this.#policyContextService.policyContext, { initialValue: null });

  readonly #resolvedPolicyContext = computed(() => this.#policyContext() ?? globalWindow.policyContext ?? null);

  readonly #policyId = computed<number | null>(() => this.#resolvedPolicyContext()?.PolicyMasterID ?? null);

  readonly #policyNumber = computed<string | null>(() => this.#resolvedPolicyContext()?.PolicyNumber ?? null);

  readonly #applicantPreviewId = computed(() => {
    const applicant = this.#evaSidebarService.applicantContext();
    return applicant?.ApplicantID && !applicant.ApplicantName ? applicant.ApplicantID : undefined;
  });

  readonly #applicantPreviewResource = rxResource({
    params: () => this.#applicantPreviewId(),
    stream: ({ params: applicantId }) =>
      from(this.#accountContextService.getApplicantSidebar(applicantId)).pipe(catchError(() => of(null))),
  });

  readonly #applicantNamePreview = computed(() => {
    const sidebar = this.#applicantPreviewResource.value();
    return sidebar?.applicant?.accountName ?? sidebar?.applicant?.name ?? null;
  });

  readonly #autoAnchor = computed<EvaAnchor | null>(() => {
    const applicant = this.#evaSidebarService.applicantContext();
    const policyId = this.#policyId();

    if (!applicant && !policyId) {
      return null;
    }

    return {
      applicantId: applicant?.ApplicantID ?? null,
      applicantName: (applicant?.ApplicantName || this.#applicantNamePreview()) ?? null,
      policyId,
      policyNumber: policyId ? this.#policyNumber() : null,
    };
  });

  readonly #lastKnownAutoAnchor = signal<EvaAnchor | null>(this.#readLocalStorage(this.#lastKnownAnchorCacheKey));

  readonly #evaResolvedContext = signal<{ applicantId: number | null; policyId: number | null } | null>(
    this.#restoredResolvedSnapshot?.context ?? null,
  );

  readonly #evaResolvedApplicantId = computed(() => this.#evaResolvedContext()?.applicantId || undefined);

  readonly #evaResolvedApplicantResource = rxResource({
    params: () => this.#evaResolvedApplicantId(),
    stream: ({ params: applicantId }) =>
      from(this.#accountContextService.getApplicantSidebar(applicantId)).pipe(catchError(() => of(null))),
  });

  readonly #evaResolvedApplicantName = computed(() => {
    const sidebar = this.#evaResolvedApplicantResource.value();
    return sidebar?.applicant?.accountName ?? sidebar?.applicant?.name ?? null;
  });

  readonly #evaResolvedPolicyId = computed(() => this.#evaResolvedContext()?.policyId || undefined);

  readonly #evaResolvedPolicyResource = rxResource({
    params: () => this.#evaResolvedPolicyId(),
    stream: ({ params: policyId }) =>
      from(this.#policyClient.getPolicyPreview(policyId)).pipe(catchError(() => of(null))),
  });

  readonly #evaResolvedPolicyPreview = computed(() => this.#evaResolvedPolicyResource.value() ?? null);

  readonly #evaResolvedAnchor = computed<EvaAnchor | null>(() => {
    const context = this.#evaResolvedContext();
    if (!context?.applicantId && !context?.policyId) {
      return null;
    }

    return {
      applicantId: context.applicantId ?? null,
      applicantName: this.#evaResolvedApplicantName(),
      policyId: context.policyId ?? null,
      policyNumber: context.policyId ? this.#evaResolvedPolicyPreview()?.policyNumber ?? null : null,
    };
  });

  readonly #priority = signal<'auto' | 'evaResolved'>(this.#restoredResolvedSnapshot?.priority ?? 'auto');
  #previousApplicantIdBeforeNav: number | null = null;
  #previousNavigatedUrl: string | null = null;
  #hasSeenInitialNavigation = false;

  readonly #pinnedAnchorCacheKey = `evaPinnedAnchor_${this.#userService.user.id}`;

  readonly #frozenAnchor = signal<EvaAnchor | null>(((): EvaAnchor | null => {
    try {
      const pinnedAnchorJson = sessionStorage.getItem(this.#pinnedAnchorCacheKey);
      return pinnedAnchorJson ? JSON.parse(pinnedAnchorJson) as EvaAnchor : null;
    } catch {
      return null;
    }
  })());

  readonly #candidate = computed<EvaAnchor | null>(() => {
    const auto = this.#autoAnchor();
    const evaResolved = this.#evaResolvedAnchor();
    const candidate = this.#pickAnchor(auto, evaResolved);
    const frozenAnchor = this.#frozenAnchor();

    return candidate && frozenAnchor
      && candidate.applicantId === frozenAnchor.applicantId
      && candidate.policyId == null
      ? { ...candidate, policyId: frozenAnchor.policyId, policyNumber: frozenAnchor.policyNumber }
      : candidate;
  });

  readonly isExplicitlyCleared = computed<boolean>(() => {
    const cleared = this.#clearedAnchorSnapshot();
    return cleared != null
      && this.#anchorKey(this.#autoAnchor()) === cleared.autoKey
      && this.#anchorKey(this.#evaResolvedAnchor()) === cleared.evaResolvedKey;
  });

  readonly activeAnchor = computed<EvaAnchor | null>(() =>
    this.isExplicitlyCleared() ? null : this.#candidate(),
  );

  readonly isAnchorVisible = computed<boolean>(() => {
    const anchor = this.activeAnchor();
    return !!anchor && !!(anchor.applicantId && anchor.applicantName || anchor.policyId);
  });

  constructor() {
    effect(() => {
      const auto = this.#autoAnchor();
      if (auto) {
        this.#lastKnownAutoAnchor.set(auto);
      }
    });

    effect(() => {
      localStorage.setItem(this.#manualOverrideCacheKey, JSON.stringify(this.#clearedAnchorSnapshot()));
    });

    effect(() => {
      localStorage.setItem(this.#lastKnownAnchorCacheKey, JSON.stringify(this.#lastKnownAutoAnchor()));
    });

    effect(() => {
      const snapshot: ResolvedContextSnapshot = {
        context: this.#evaResolvedContext(),
        priority: this.#priority(),
      };
      sessionStorage.setItem(this.#resolvedContextCacheKey, JSON.stringify(snapshot));
    });

    if (this.#router) {
      this.#router.events.pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.#destroyRef),
      ).subscribe((event) => {
        if (!this.#hasSeenInitialNavigation) {
          this.#hasSeenInitialNavigation = true;
          this.#previousApplicantIdBeforeNav = this.#autoAnchor()?.applicantId ?? null;
          this.#previousNavigatedUrl = event.urlAfterRedirects;
          return;
        }

        const applicantId = this.#autoAnchor()?.applicantId ?? null;
        const sameAccountAsBefore = applicantId === this.#previousApplicantIdBeforeNav;
        const sameUrlAsBefore = event.urlAfterRedirects === this.#previousNavigatedUrl;

        const isLateralTabMove = sameAccountAsBefore && !sameUrlAsBefore;
        if (!isLateralTabMove) {
          this.#priority.set('auto');
        }

        this.#previousApplicantIdBeforeNav = applicantId;
        this.#previousNavigatedUrl = event.urlAfterRedirects;
      });
    }

    this.#evaSidebarService.onNewChat$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(() => {
      this.#resetFields();
    });

    this.#evaSidebarService.onClosePanel$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(() => {
      this.#resetFields();
    });
  }

  contextForQuery(): EvaAnchor | null {
    return this.activeAnchor();
  }

  pinCurrentAnchorThroughNavigation(): void {
    this.#frozenAnchor.set(this.activeAnchor());
    sessionStorage.setItem(this.#pinnedAnchorCacheKey, JSON.stringify(this.#frozenAnchor()));
  }

  setResolvedContext(
    context: { applicantId?: number | null; policyId?: number | null } | null | undefined,
    options?: { reassertPriority?: boolean },
  ): void {
    const applicantId = context?.applicantId === -1 ? null : context?.applicantId ?? null;
    const policyId = context?.policyId === -1 ? null : context?.policyId ?? null;

    if (!applicantId && !policyId) {
      this.#evaResolvedContext.set(null);
      return;
    }

    this.#evaResolvedContext.set({ applicantId, policyId });
    if (options?.reassertPriority ?? true) {
      this.#priority.set('evaResolved');
    }
  }

  clearAnchor(): void {
    const active = this.activeAnchor();

    const resolved = this.#evaResolvedContext();
    if (resolved) {
      const applicantId = active?.applicantId != null && active.applicantId === resolved.applicantId ? null : resolved.applicantId;
      const policyId = active?.policyId != null && active.policyId === resolved.policyId ? null : resolved.policyId;
      this.#evaResolvedContext.set(applicantId == null && policyId == null ? null : { applicantId, policyId });
    }

    this.#clearedAnchorSnapshot.set({
      autoKey: this.#anchorKey(this.#autoAnchor()),
      evaResolvedKey: this.#anchorKey(this.#evaResolvedAnchor()),
    });
  }

  #pickAnchor(auto: EvaAnchor | null, evaResolved: EvaAnchor | null): EvaAnchor | null {
    const conflict = auto != null && evaResolved != null && this.#anchorKey(auto) !== this.#anchorKey(evaResolved);
    if (!conflict) {
      const agreedAnchor = auto ?? evaResolved;
      if (agreedAnchor) {
        return agreedAnchor;
      }

      const cached = this.#lastKnownAutoAnchor();
      if (!cached) {
        return null;
      }

      return this.#policyId() != null ? cached : { ...cached, policyId: null, policyNumber: null };
    }

    return this.#priority() === 'evaResolved' ? evaResolved : auto;
  }

  #anchorKey(anchor: EvaAnchor | null): string | null {
    return anchor ? `${anchor.applicantId ?? ''}|${anchor.policyId ?? ''}` : null;
  }

  #readLocalStorage<T>(key: string): T | null {
    try {
      const cachedJson = localStorage.getItem(key);
      return cachedJson ? JSON.parse(cachedJson) as T : null;
    } catch {
      return null;
    }
  }

  #readSessionCache<T>(key: string): T | null {
    try {
      const cachedJson = sessionStorage.getItem(key);
      return cachedJson ? JSON.parse(cachedJson) as T : null;
    } catch {
      return null;
    }
  }

  #resetFields(): void {
    this.#clearedAnchorSnapshot.set(null);
    this.#lastKnownAutoAnchor.set(null);
    this.#evaResolvedContext.set(null);
    this.#priority.set('auto');
    this.#frozenAnchor.set(null);
    sessionStorage.removeItem(this.#pinnedAnchorCacheKey);
  }
}
